import {
  Alert,
  Button,
  Empty,
  Input,
  InputNumber,
  Select,
  Popconfirm,
  Space,
  Switch,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { ApiService } from "../../api";
import { useTranslation } from "react-i18next";
import {
  HAS_OPTIONS,
  PROFILE_KEYS,
  containerLabel,
  isColumn,
  isContainer,
  isTable,
  widgetLabel,
  type FieldDefault,
  type Node,
} from "./schema";
import RichTextInput from "../RichTextInput";
import RulesEditor from "./RulesEditor";
import type { Rules } from "./rules";

const { Text, Title } = Typography;

type Props = {
  node: Node | null;
  onChange: (patch: Partial<Node>) => void;
  onRemove: () => void;
  /** The questions asked BEFORE this one, for the "show only when…" picker.
   *  Earlier only: a field cannot depend on an answer given below it, and
   *  offering the later ones invites a rule that can never be true. */
  earlierFields?: Array<{ key: string; title: string; options: string[] }>;
  /** Add a column to the selected repeating table. Columns cannot be dragged in
   *  from the palette: a column is a wrapper around exactly one field, and a
   *  loose one outside a table is a node the renderer does nothing with. */
  onAddColumn?: () => void;
};

const Field = ({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) => (
  <div style={{ marginBottom: 14 }}>
    <div style={{ marginBottom: 4 }}>
      {hint ? (
        <Tooltip title={hint}>
          <Text
            type="secondary"
            style={{ fontSize: 12, borderBottom: "1px dotted" }}
          >
            {label}
          </Text>
        </Tooltip>
      ) : (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {label}
        </Text>
      )}
    </div>
    {children}
  </div>
);

/** The settings for the selected field. */
const FieldInspector = ({
  node,
  onChange,
  onRemove,
  earlierFields = [],
  onAddColumn,
}: Props) => {
  const { t } = useTranslation();
  // Fetched once, and only when a lookup field is actually selected: most
  // forms have none, and a request on every selection would be a request per
  // click for nothing.
  const [entityTypes, setEntityTypes] = useState<
    Array<{ code: string; name: string; entity_count: number }>
  >([]);
  const wantsLists = node?.component === "EntitySelect";
  useEffect(() => {
    if (!wantsLists || entityTypes.length) return;
    ApiService.apiCatalogEntityTypes()
      .then((rows) => setEntityTypes(rows ?? []))
      .catch(() => setEntityTypes([]));
  }, [wantsLists, entityTypes.length]);

  if (!node) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={t("Pick a field to edit it.")}
        style={{ marginTop: 40 }}
      />
    );
  }

  const container = isContainer(node);
  const options = node.enumOptions ?? [];

  const setOption = (index: number, patch: Partial<{ label: string; value: string }>) =>
    onChange({
      enumOptions: options.map((o, i) => (i === index ? { ...o, ...patch } : o)),
    });

  return (
    <div>
      <Space
        style={{ width: "100%", justifyContent: "space-between" }}
        align="start"
      >
        <div>
          <Title level={5} style={{ margin: 0 }}>
            {containerLabel(node) || node.title || node.key}
          </Title>
          <Tag style={{ marginTop: 6 }}>
            {container ? node.component : t(widgetLabel(node.component))}
          </Tag>
        </div>
        <Popconfirm
          title={
            container
              ? t("Remove this container and everything in it?")
              : t("Remove this field?")
          }
          description={t(
            "Answers already saved against it stay in the database but stop being shown."
          )}
          okText={t("Remove")}
          cancelText={t("Cancel")}
          onConfirm={onRemove}
        >
          <Button
            size="small"
            type="text"
            danger
            icon={<DeleteOutlined />}
            aria-label={t("Remove")}
          />
        </Popconfirm>
      </Space>

      <div style={{ marginTop: 14 }}>
        {/* A card's heading, a column's heading and a line of static text are
            all `x-component-props`, not the schema-level `title` — different
            keys, and only the first renders. Editing `title` on a card changed
            nothing an administrator could see. */}
        {node.component === "Markdown" ? (
          /* A guidance block is edited with the same control the person filling
             the form in would use to write one, rather than a bare textarea:
             the administrator writing a warning needs to see the warning. */
          <Field
            label={t("Guidance")}
            hint={t("Shown to whoever fills the form in. It asks nothing and stores nothing.")}
          >
            <RichTextInput
              value={node.componentProps.content ?? ""}
              onChange={(content: string) =>
                onChange({
                  componentProps: { ...node.componentProps, content },
                })
              }
            />
          </Field>
        ) : ["Card", "ArrayTable.Column", "Text"].includes(node.component) ? (
          <Field
            label={
              node.component === "Text" ? t("Text") : t("Heading")
            }
            hint={
              node.component === "Text"
                ? t("Shown to whoever fills the form in. It asks nothing and stores nothing.")
                : undefined
            }
          >
            <Input
              value={
                node.component === "Text"
                  ? node.componentProps.content ?? ""
                  : node.componentProps.title ?? ""
              }
              onChange={(e) =>
                onChange({
                  componentProps: {
                    ...node.componentProps,
                    [node.component === "Text" ? "content" : "title"]:
                      e.target.value,
                  },
                })
              }
            />
          </Field>
        ) : (
          <Field label={t("Label")}>
            <Input
              value={node.title}
              placeholder={t("What the person filling this in reads")}
              onChange={(e) => onChange({ title: e.target.value })}
            />
          </Field>
        )}

        {!["Card", "FormGrid", "FormGrid.GridColumn", "Text", "Markdown"].includes(
          node.component
        ) && (
          <Field
            label={t("Field name")}
            hint={t(
              "The name answers are stored against. It cannot be changed here, because renaming it would hide every answer already recorded under the old name."
            )}
          >
            <Input value={node.key} disabled />
          </Field>
        )}

        {node.component === "FormGrid" && (
          <Field
            label={t("Columns across")}
            hint={t(
              "How many columns the fields inside are laid out in on a wide screen. It drops to one on a narrow one by itself."
            )}
          >
            <InputNumber
              min={1}
              max={6}
              style={{ width: "100%" }}
              value={node.componentProps.maxColumns ?? 2}
              onChange={(v) =>
                onChange({
                  componentProps: {
                    ...node.componentProps,
                    maxColumns: v ?? 2,
                    minColumns: node.componentProps.minColumns ?? 1,
                  },
                })
              }
            />
          </Field>
        )}

        {node.component === "FormGrid.GridColumn" && (
          <Field
            label={t("Width")}
            hint={t(
              "How many of the grid's columns this one spans. Leave it at 1 for an ordinary column."
            )}
          >
            <InputNumber
              min={1}
              max={6}
              style={{ width: "100%" }}
              value={node.componentProps.gridSpan ?? 1}
              onChange={(v) =>
                onChange({
                  componentProps: { ...node.componentProps, gridSpan: v ?? 1 },
                })
              }
            />
          </Field>
        )}

        {isColumn(node) && (
          <Field label={t("Column width (px)")}>
            <InputNumber
              min={60}
              max={600}
              style={{ width: "100%" }}
              value={node.componentProps.width ?? 160}
              onChange={(v) =>
                onChange({
                  componentProps: { ...node.componentProps, width: v ?? 160 },
                })
              }
            />
          </Field>
        )}

        {isTable(node) && (
          <Field
            label={t("Columns")}
            hint={t(
              "Each column holds one question, asked once per row. Pick a column on the canvas to rename it or change what it asks."
            )}
          >
            <Button
              size="small"
              icon={<PlusOutlined />}
              onClick={() => onAddColumn?.()}
            >
              {t("Add a column")}
            </Button>
          </Field>
        )}

        {!container && (
          <>
            <Field label={t("Placeholder")}>
              <Input
                value={node.componentProps.placeholder ?? ""}
                onChange={(e) =>
                  onChange({
                    componentProps: {
                      ...node.componentProps,
                      placeholder: e.target.value,
                    },
                  })
                }
              />
            </Field>

            <Space size={8} style={{ marginBottom: 14 }}>
              <Switch
                checked={node.required}
                onChange={(v) => onChange({ required: v })}
              />
              <Text>{t("Required")}</Text>
            </Space>
          </>
        )}

        {node.component === "EntitySelect" && (
          <Field
            label={t("Which list?")}
            hint={t(
              "The maintained list this field picks from. Lists are set up under Master data; the answer is stored as the entry's code, so renaming an entry never breaks an answer already given."
            )}
          >
            <Select
              style={{ width: "100%" }}
              value={node.componentProps.entityType || undefined}
              placeholder={t("Pick a list")}
              status={node.componentProps.entityType ? undefined : "warning"}
              onChange={(v) =>
                onChange({
                  componentProps: { ...node.componentProps, entityType: v },
                })
              }
              options={entityTypes.map((e) => ({
                value: e.code,
                label: `${e.name} (${e.entity_count})`,
              }))}
              notFoundContent={t(
                "No lists yet. An administrator creates them under Master data."
              )}
            />
          </Field>
        )}

        {node.component === "Upload" && (
          <Field
            label={t("Camera")}
            hint={t(
              "Adds a Take photo button that opens the camera. Turn it off where a photograph taken on the spot is not acceptable evidence — a scanned certificate, say."
            )}
          >
            <Space size={8}>
              <Switch
                // Absent means on: the field is older than this setting, and
                // every deployment that had one gained the button by default.
                checked={node.componentProps.camera !== false}
                onChange={(v) =>
                  onChange({
                    componentProps: { ...node.componentProps, camera: v },
                  })
                }
              />
              <Text>
                {node.componentProps.camera === false
                  ? t("Files only")
                  : t("Allow photos")}
              </Text>
            </Space>
          </Field>
        )}

        {HAS_OPTIONS.has(node.component) && (
          <Field
            label={t("Options")}
            hint={t(
              "The value is what gets stored; the label is what people see. Changing a value orphans answers already recorded with the old one."
            )}
          >
            <Space direction="vertical" size={6} style={{ width: "100%" }}>
              {options.map((option, i) => (
                <Space.Compact key={i} style={{ width: "100%" }}>
                  <Input
                    style={{ width: "50%" }}
                    value={option.label}
                    placeholder={t("Label")}
                    onChange={(e) => setOption(i, { label: e.target.value })}
                  />
                  <Input
                    style={{ width: "50%" }}
                    value={option.value}
                    placeholder={t("Value")}
                    onChange={(e) => setOption(i, { value: e.target.value })}
                  />
                  <Button
                    icon={<DeleteOutlined />}
                    aria-label={t("Remove")}
                    onClick={() =>
                      onChange({
                        enumOptions: options.filter((_, x) => x !== i),
                      })
                    }
                  />
                </Space.Compact>
              ))}
              <Button
                size="small"
                icon={<PlusOutlined />}
                onClick={() =>
                  onChange({
                    enumOptions: [
                      ...options,
                      {
                        label: `Option ${options.length + 1}`,
                        value: `option${options.length + 1}`,
                      },
                    ],
                  })
                }
              >
                {t("Add an option")}
              </Button>
            </Space>
          </Field>
        )}

        {/* Where the field starts (C10). Every requester retypes their own
            department and site onto every case, and those are two facts the
            system already knows about them. */}
        {!container && !["Text", "Markdown"].includes(node.component) && (
          <Field
            label={t("Starts as")}
            hint={t(
              "What the field already contains when somebody opens a new case. They can still change it — a default is a head start, not an answer."
            )}
          >
            <Select
              style={{ width: "100%" }}
              value={node.defaultValue?.source ?? "none"}
              onChange={(source) => {
                if (source === "none") return onChange({ defaultValue: null });
                if (source === "today")
                  return onChange({ defaultValue: { source: "today" } });
                if (source === "fixed")
                  return onChange({
                    defaultValue: { source: "fixed", value: "" },
                  });
                onChange({
                  defaultValue: { source: "profile", key: "department" },
                });
              }}
              options={[
                { value: "none", label: t("Empty") },
                { value: "fixed", label: t("A fixed value") },
                { value: "profile", label: t("Something about the requester") },
                { value: "today", label: t("Today's date") },
              ]}
            />

            {node.defaultValue?.source === "fixed" && (
              <Input
                style={{ marginTop: 8 }}
                value={String(node.defaultValue.value ?? "")}
                placeholder={t("The value it starts with")}
                onChange={(e) =>
                  onChange({
                    defaultValue: { source: "fixed", value: e.target.value },
                  })
                }
              />
            )}

            {node.defaultValue?.source === "profile" && (
              <Select
                style={{ width: "100%", marginTop: 8 }}
                value={node.defaultValue.key}
                onChange={(key) =>
                  onChange({ defaultValue: { source: "profile", key } })
                }
                options={PROFILE_KEYS.map((p) => ({
                  value: p.key,
                  label: t(p.label),
                }))}
              />
            )}

            {node.defaultValue?.source === "profile" && (
              <Text
                type="secondary"
                style={{ fontSize: 11, display: "block", marginTop: 6 }}
              >
                {/* Said out loud because the alternative reading -- that the
                    value is fixed into the form when it is designed -- is the
                    wrong one, and it is the reading somebody will have. */}
                {t(
                  "Filled in for whoever opens the case, not for you. It stays empty if their record does not have it."
                )}
              </Text>
            )}
          </Field>
        )}

        {!container && (
          <RulesEditor
            node={node}
            others={earlierFields}
            onChange={(rules: Rules) => onChange({ rules })}
          />
        )}

        {container && (
          <Alert
            type="info"
            showIcon
            message={t(
              "This is a layout container. Drag fields into it on the canvas."
            )}
          />
        )}
      </div>
    </div>
  );
};

export default FieldInspector;
